import type { Company } from "@prisma/client";
import type { MetaFunction } from "@remix-run/react";
import { Form, useActionData, useLoaderData } from "@remix-run/react";
import type { ActionFunction, LoaderFunction } from "@remix-run/server-runtime";
import companyRepository from "~/repositories/companyRepository";
import authenticateWithPassword from "~/kernel/auth/authenticateWithPassword.server";
import createUserSession from "~/kernel/auth/createUserSession.server";
import { translationHandle } from "~/kernel/internationalization";
import useTranslation from "~/kernel/internationalization/useTranslation";
import parseFormData from "~/kernel/form/parseFormData";
import ROUTES from "~/kernel/routing/ROUTES";
import { createAction, createLoader } from "~/kernel/server";
import requireResource from "~/kernel/server/requireResource";
import type { ActionData as AlertActionData } from "~/ui/ActionAlert";
import ActionAlert from "~/ui/ActionAlert";
import Breadcrumb from "~/ui/Breadcrumb";
import Button from "~/ui/Button";
import Card from "~/ui/Card";
import Heading from "~/ui/Heading";
import Link from "~/ui/Link";
import SignInFormFields from "~/kernel/auth/components/SignInFormFields";
import isNil from "~/kernel/validation/isNil";
import { FormFieldsWrapper, SpacedContent, Text } from "~/ui";
import useAnalytics from "~/analytics/components/useAnalytics";
import useEffectOnce from "~/kernel/effects/useEffectOnce";

export type LoaderData = {
  company: Company;
  pageTitle: string;
};

type ActionData = AlertActionData & {
  email?: string;
};

export const handle = translationHandle({
  ns: { bookingSignIn: true, authentication: true },
});

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: data?.pageTitle },
];

export const loader: LoaderFunction = createLoader(
  async ({ params, json, translation }) => {
    const slug = params.slug;
    requireResource(slug, { errorMessage: "Företag kunde inte hittas" });
    const company = await companyRepository.findBySlug(slug);

    if (isNil(company)) {
      throw new Error("Företag kunde inte hittas");
    }

    return json<LoaderData>({
      company,
      pageTitle: translation.tt("pageTitle", { companyName: company.name }),
    });
  },
  { handle, translationScope: "bookingSignIn" },
);

export const action: ActionFunction = createAction(
  async ({ request, params, json, translation }) => {
    const slug = params.slug;
    requireResource(slug, { errorMessage: "Företag kunde inte hittas" });
    const formDataResult = await parseFormData(request);

    if (!formDataResult.isSuccessful) {
      return json<ActionData>({
        errorMessage: translation.tt("errors.INVALID_EMAIL"),
      });
    }

    const { formData } = formDataResult;
    const authResult = await authenticateWithPassword({
      email: formData.get("email"),
      password: formData.get("password"),
    });

    if (authResult.isSuccessful) {
      return createUserSession({
        request,
        userId: authResult.unsafeUnwrap().id,
        redirectUrl: ROUTES.newBooking.path({ params: { slug } }),
      });
    }

    const { code, email } = authResult.unwrapErr();

    return json<ActionData>({
      email,
      errorMessage: translation.tt(`errors.${code}`),
    });
  },
  { handle, translationScope: "bookingSignIn" },
);

export default function BookingSignInPage() {
  const { company } = useLoaderData<LoaderData>();
  const actionData = useActionData<ActionData>();
  const { tt } = useTranslation("bookingSignIn");
  const analytics = useAnalytics();
  const redirectUrl = ROUTES.newBooking.path({
    params: { slug: company.slug },
  });
  const payload = { companyId: company.id, companySlug: company.slug };

  useEffectOnce(() => {
    analytics.track({ type: "SIGN_IN_FROM_BOOKING_VIEWED_V1", payload });
  });

  return (
    <div className="m-auto max-w-xl space-y-3">
      <Breadcrumb
        links={[
          {
            text: company.name,
            to: ROUTES.company.path({ params: { slug: company.slug } }),
          },
          {
            text: tt("bookingAt", { companyName: company.name }),
            to: ROUTES.newBooking.path({ params: { slug: company.slug } }),
          },
        ]}
      />
      <div className="m-auto max-w-2xl">
        <Card>
          <SpacedContent>
            <div className="space-y-2">
              <Heading>{tt("heading")}</Heading>

              <div className="max-w-sm">
                <Text variant="bodyMedium" color="muted">
                  {tt("whySignIn")}
                </Text>
              </div>
            </div>

            <ActionAlert actionData={actionData} />

            <Form
              method="post"
              onSubmit={() =>
                analytics.track({
                  type: "SIGN_IN_FROM_BOOKING_V1",
                  payload,
                })
              }
            >
              <FormFieldsWrapper>
                <SignInFormFields defaultEmail={actionData?.email} />
                <Button type="submit" children={tt("signIn")} />
                <p className="pt-2 text-center">
                  {tt("noAccount")}{" "}
                  <Link
                    to={ROUTES.customerSignUp.path({ query: { redirectUrl } })}
                    onClick={() =>
                      analytics.track({
                        type: "SIGN_UP_FROM_BOOKING_V1",
                        payload,
                      })
                    }
                  >
                    {tt("noAccountAction")}
                  </Link>
                </p>
              </FormFieldsWrapper>
            </Form>
          </SpacedContent>
        </Card>
      </div>
    </div>
  );
}
